import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, MessageCircle, Send, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import { useLanguage } from '../i18n/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('contact.error', 'Failed to send message. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} setUser={setUser} />
      
      <div className="px-6 md:px-12 lg:px-16 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-text-primary mb-4" data-testid="contact-page-title">
              {t('contact.title', 'Get in touch')}
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              {t('contact.subtitle', "Have a question, feedback, or need help with a booking? We'd love to hear from you.")}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-surface rounded-2xl p-6 border border-border">
                <div className="p-3 bg-primary-light rounded-full w-fit mb-4">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-text-primary mb-1">{t('contact.infoEmailLabel', 'Email us')}</h3>
                <a
                  href="mailto:office@matchmycleaner.com"
                  className="text-sm text-primary hover:underline"
                  data-testid="contact-email-link"
                >
                  office@matchmycleaner.com
                </a>
                <p className="text-xs text-text-secondary mt-1">{t('contact.replyTime', 'We reply within 24 hours')}</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-surface rounded-2xl p-8 border border-border" data-testid="contact-form-card">
                {submitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <CheckCircle className="w-16 h-16 text-success mx-auto mb-6" />
                    <h3 className="font-heading text-2xl font-bold text-text-primary mb-3">{t('contact.sentTitle', 'Message sent!')}</h3>
                    <p className="text-text-secondary mb-8">
                      {t('contact.success', `Thanks for reaching out. We'll get back to you within 24 hours.`)}
                    </p>
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() => {
                          setSubmitted(false);
                          setFormData({ name: user?.name || '', email: user?.email || '', subject: '', message: '' });
                        }}
                        className="btn-secondary"
                      >
                        {t('contact.sendAnother', 'Send another message')}
                      </button>
                      <button onClick={() => navigate('/')} className="btn-primary" data-testid="back-home-button">
                        {t('contact.backHome', 'Back to home')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-primary-light rounded-full">
                        <MessageCircle className="w-5 h-5 text-primary" />
                      </div>
                      <h2 className="font-heading text-2xl font-semibold text-text-primary">{t('contact.formTitle', 'Send us a message')}</h2>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">{t('contact.name', 'Your Name')}</label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="input-field w-full"
                            placeholder={t('contact.namePlaceholder', 'Jane Smith')}
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">{t('contact.email', 'Email Address')}</label>
                          <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="input-field w-full"
                            placeholder={t('contact.emailPlaceholder', 'jane@example.com')}
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">{t('contact.subject', 'Subject')}</label>
                        <select
                          required
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          className="input-field w-full"
                          data-testid="contact-subject-select"
                        >
                          <option value="">{t('contact.subjectPlaceholder', 'Choose a topic')}</option>
                          <option value="Booking Issue">{t('contact.topicBooking', 'Booking Issue')}</option>
                          <option value="Payment Question">{t('contact.topicPayment', 'Payment Question')}</option>
                          <option value="Become a Cleaner">{t('contact.topicJoin', 'Become a Cleaner')}</option>
                          <option value="Feedback">{t('contact.topicFeedback', 'Feedback')}</option>
                          <option value="Partnership">{t('contact.topicPartnership', 'Partnership Inquiry')}</option>
                          <option value="Other">{t('contact.topicOther', 'Other')}</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">{t('contact.message', 'Message')}</label>
                        <textarea
                          required
                          rows="6"
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          className="input-field w-full resize-none"
                          placeholder={t('contact.messagePlaceholder', 'Tell us how we can help...')}
                          data-testid="contact-message-input"
                        />
                      </div>

                      {error && (
                        <div className="bg-red-50 border border-error text-error px-4 py-3 rounded-xl text-sm" data-testid="contact-error">
                          {error}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary w-full flex items-center justify-center gap-2"
                        data-testid="contact-submit-button"
                      >
                        {loading ? t('contact.submitting', 'Sending...') : (
                          <>
                            <Send className="w-4 h-4" />
                            {t('contact.submit', 'Send Message')}
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
